import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse } from "../../../types/common";
import { ICompany, ICompanyUpdatePayload } from "../../../types/api/company";
import { getMyCompany, updateMyCompany } from "./callbacks";

export const useGetMyCompany = (
  options?: Omit<UseQueryOptions<TResponse<ICompany>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<ICompany>, Error> => {
  return useQuery<TResponse<ICompany>, Error>({
    queryKey: [API_ENDPOINTS.MY_COMPANY],
    queryFn: async () => {
      const res = await getMyCompany();
      return res.data;
    },
    staleTime: 300000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useUpdateMyCompany = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<ICompany>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<ICompany>, unknown, ICompanyUpdatePayload> = {}
): UseMutationResult<TResponse<ICompany>, unknown, ICompanyUpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<ICompany>, unknown, ICompanyUpdatePayload>({
    mutationFn: async (data: ICompanyUpdatePayload) => {
      const res = await updateMyCompany(data);
      return res.data;
    },
    onSuccess: (res: TResponse<ICompany>, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.MY_COMPANY], res);
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};
